import { computed, ref, unref, type ComputedRef, type Ref } from 'vue';
import {
    deriveClientWorkspaceOnboardingState,
    deriveDeliveryStageFromClientLifecycle,
    derivePipelineStageFromClientLifecycle,
} from '~/lib/clientWorkspaceDetail';
import { recommendClientOnboardingTemplateId } from '~/lib/clientOnboarding';
import { toMillis } from '~/lib/timestamps';
import { toast } from 'vue-sonner';
import type {
    Client,
    ClientLifecycle,
    OnboardingRun,
    Project,
    ClientDeliveryStage,
    ClientPipelineStage,
    ClientCommercialStage,
} from '~/types/models';

type MaybeRef<T> = Ref<T> | ComputedRef<T>;

export type ClientWorkspaceOnboardingPayload = {
    templateId: string;
    clientName: string;
    projectMode: 'existing' | 'new' | 'skip';
    projectTemplateId?: string;
    existingProjectId?: string;
    projectName: string;
    projectDescription?: string;
    ownerId?: string;
    ownerName?: string;
    sendWelcomeEmail: boolean;
};

interface Options {
    client: MaybeRef<Client | null>;
    relatedProjects: MaybeRef<Project[]>;
    relatedRuns: MaybeRef<OnboardingRun[]>;
    onboardingActive: MaybeRef<OnboardingRun[]>;
    onboardingTemplates: MaybeRef<Array<{ id: string; name?: string }>>;
    canMutate: MaybeRef<boolean>;
    userStore: any;
    clientStore: any;
    projectStore: any;
    onboardingStore: any;
    onboardingHandoffOpen: Ref<boolean>;
    updateClientLifecycle: (patch: Partial<ClientLifecycle>) => Promise<void>;
    refreshResolvedClient: () => Promise<void>;
    tenantPath: (path: string) => string;
    projectHref: (project: Project) => string | null;
    pipelineStageLabels: Record<ClientPipelineStage, string>;
    shouldPromptOnWon: (client: Client | null | undefined) => boolean;
}

export function useClientWorkspaceLifecycle(options: Options) {
    const stageSaving = ref(false);
    const startingOnboarding = ref(false);

    const highlightedRun = computed<OnboardingRun | null>(() => (
        unref(options.onboardingActive)[0] || unref(options.relatedRuns)[0] || null
    ));
    const onboardingState = computed(() => deriveClientWorkspaceOnboardingState({
        clientLifecycle: unref(options.client)?.lifecycle,
        hasActiveRun: Boolean(highlightedRun.value),
    }));
    const onboardingSkipped = computed(() => onboardingState.value.onboardingSkipped);
    const currentRunProject = computed<Project | null>(() => {
        const projectId = highlightedRun.value?.projectId;
        if (!projectId) return null;
        return unref(options.relatedProjects).find((project) => project.id === projectId) || null;
    });
    const recommendedOnboardingTemplateId = computed(() => recommendClientOnboardingTemplateId({
        client: unref(options.client) || undefined,
        projectNames: unref(options.relatedProjects).map((project) => project.name),
    }));
    const recommendedOnboardingTemplate = computed(() => unref(options.onboardingTemplates).find(
        (template) => template.id === recommendedOnboardingTemplateId.value,
    ) || null);
    const readyToStartOnboarding = computed(() => onboardingState.value.readyToStartOnboarding);
    const clientHeaderStatusLabel = computed(() => {
        const deliveryStage = deriveDeliveryStageFromClientLifecycle(unref(options.client)?.lifecycle);
        const pipelineStage = derivePipelineStageFromClientLifecycle(unref(options.client)?.lifecycle);
        if (highlightedRun.value) return 'Active onboarding';
        if (onboardingSkipped.value) return 'Onboarding skipped';
        if (readyToStartOnboarding.value) return 'Ready for onboarding';
        if (deliveryStage === 'active') return 'Active delivery';
        if (deliveryStage === 'paused') return 'Delivery paused';
        return options.pipelineStageLabels[pipelineStage];
    });
    const onboardingStartedAt = computed(() => highlightedRun.value?.startedAt || highlightedRun.value?.createdAt || null);
    const daysInOnboarding = computed(() => {
        if (!onboardingStartedAt.value) return null;
        const diff = Date.now() - toMillis(onboardingStartedAt.value);
        if (!Number.isFinite(diff) || diff < 0) return 0;
        return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    });
    const overviewControlsOnboardingTitle = computed(() => {
        if (highlightedRun.value) return highlightedRun.value.templateName || 'Active onboarding run';
        if (onboardingSkipped.value) return 'Onboarding skipped';
        if (readyToStartOnboarding.value) return 'Ready for onboarding handoff';
        return 'No onboarding run linked yet';
    });
    const overviewControlsOnboardingDetail = computed(() => {
        if (highlightedRun.value) {
            const days = daysInOnboarding.value;
            return `${highlightedRun.value.currentStageId?.replaceAll('_', ' ') || 'Preparing handoff'}${days != null ? ` • ${days} day${days === 1 ? '' : 's'}` : ''}`;
        }
        if (onboardingSkipped.value) return 'This client was moved forward without creating an onboarding run.';
        if (readyToStartOnboarding.value) {
            return `Recommended pipeline: ${recommendedOnboardingTemplate.value?.name || 'General client onboarding'}`;
        }
        return derivePipelineStageFromClientLifecycle(unref(options.client)?.lifecycle) === 'won'
            ? 'This client is won. Start or skip onboarding from here before active delivery begins.'
            : 'Move this client to Won in the lifecycle controls above to unlock Start onboarding and Skip onboarding.';
    });
    const overviewControlsOnboardingHref = computed(() => highlightedRun.value ? options.tenantPath('/onboarding') : '');
    const overviewControlsProjectHref = computed(() => currentRunProject.value ? (options.projectHref(currentRunProject.value) || '') : '');

    async function updateClientCommercialStages(payload: Partial<ClientLifecycle>) {
        const client = unref(options.client);
        if (!client?.id) return;
        stageSaving.value = true;
        try {
            await options.updateClientLifecycle(payload);
            toast.success('Client stage updated.');
        } catch (error: any) {
            toast.error(error?.message || 'Could not update client stage.');
        } finally {
            stageSaving.value = false;
        }
    }

    async function applyPipelineStage(stage: ClientPipelineStage) {
        const client = unref(options.client);
        if (!client?.id) return;
        const nextClientCommercialStage: ClientCommercialStage = stage === 'won'
            ? 'client'
            : ['qualified', 'proposal_sent', 'negotiation'].includes(stage)
                ? 'opportunity'
                : 'lead';
        const nextDeliveryStage: ClientDeliveryStage = stage === 'won' && highlightedRun.value
            ? 'onboarding'
            : 'pre_engagement';
        stageSaving.value = true;
        try {
            await options.updateClientLifecycle({
                pipelineStage: stage,
                commercialStage: nextClientCommercialStage,
                deliveryStage: nextDeliveryStage,
                ...(stage !== 'won'
                    ? { onboardingSkippedAt: null, onboardingSkippedBy: null, onboardingSkippedByName: null }
                    : {}),
            });
            if (stage === 'won' && !highlightedRun.value && options.shouldPromptOnWon(client)) {
                options.onboardingHandoffOpen.value = true;
            }
            toast.success(`Client moved to ${options.pipelineStageLabels[stage]}.`);
        } catch (error: any) {
            toast.error(error?.message || 'Could not update pipeline stage.');
        } finally {
            stageSaving.value = false;
        }
    }

    async function handleSkipOnboarding() {
        const client = unref(options.client);
        if (!client?.id || !unref(options.canMutate)) return;
        startingOnboarding.value = true;
        try {
            const skippedAt = new Date();
            await options.updateClientLifecycle({
                deliveryStage: 'active',
                status: 'active',
                onboardingSkippedAt: skippedAt,
                onboardingSkippedBy: options.userStore.user?.id || options.userStore.userId || undefined,
                onboardingSkippedByName: options.userStore.user?.displayName || options.userStore.user?.fullName || undefined,
            });
            toast.success('Onboarding skipped for this client.');
        } catch (error: any) {
            toast.error(error?.message || 'Could not skip onboarding.');
        } finally {
            startingOnboarding.value = false;
        }
    }

    async function handleStartOnboarding(payload: ClientWorkspaceOnboardingPayload) {
        const client = unref(options.client);
        if (!client?.id) return;
        startingOnboarding.value = true;
        try {
            await options.clientStore.startOnboarding(client.id, payload);
            await Promise.all([
                options.clientStore.fetchClients(),
                options.projectStore.fetchProjects(),
                options.onboardingStore.fetchRuns(),
            ]);
            await options.refreshResolvedClient();
            options.onboardingHandoffOpen.value = false;
            toast.success('Onboarding started for this client.');
        } catch (error: any) {
            toast.error(error?.data?.statusMessage || error?.message || 'Could not start onboarding.');
        } finally {
            startingOnboarding.value = false;
        }
    }

    return {
        stageSaving,
        startingOnboarding,
        highlightedRun,
        onboardingState,
        onboardingSkipped,
        currentRunProject,
        recommendedOnboardingTemplateId,
        recommendedOnboardingTemplate,
        readyToStartOnboarding,
        clientHeaderStatusLabel,
        onboardingStartedAt,
        daysInOnboarding,
        overviewControlsOnboardingTitle,
        overviewControlsOnboardingDetail,
        overviewControlsOnboardingHref,
        overviewControlsProjectHref,
        updateClientCommercialStages,
        applyPipelineStage,
        handleSkipOnboarding,
        handleStartOnboarding,
    };
}
